import { useState } from 'react'
import { MapPin, MessageSquare, Send } from 'lucide-react'
import Seo from '../components/Seo'
import { submitForm } from '../services/formService'

import { DEMO_SURVEY_URL } from '../config/links'

const SURVEY_URL = DEMO_SURVEY_URL

export default function Contact() {
  const [formData, setFormData] = useState({ name: '', email: '', company: '', message: '' })
  const [status, setStatus] = useState({ type: '', message: '' })
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    setStatus({ type: '', message: '' })
    const result = await submitForm({ type: 'contact', data: formData })
    if (result.success) {
      setStatus({ type: 'success', message: 'Thanks! We\'ll be in touch soon.' })
      setFormData({ name: '', email: '', company: '', message: '' })
    } else {
      setStatus({ type: 'error', message: result.error })
    }
    setSubmitting(false)
  }

  return (
    <div>
      <Seo
        title="Contact"
        description="Get in touch with the PrismScope team to discuss closing the feedback loop for your organization. Based in Madison, Wisconsin."
        path="/contact"
      />

      {/* Hero Section */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-intelligence-900 mb-4">
            Get in Touch
          </h1>
          <p className="text-xl text-intelligence-600 max-w-2xl mx-auto">
            Tell us about your needs and we&apos;ll show you how PrismScope can help.
          </p>
        </div>
      </section>

      {/* Contact Options */}
      <section className="bg-intelligence-50 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Conversational AI — PrismScope experience */}
            <div className="bg-white rounded-xl border border-intelligence-200 p-8 md:p-12 text-center flex flex-col">
              <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <MessageSquare className="w-8 h-8 text-primary-600" />
              </div>
              <h2 className="text-2xl font-bold text-intelligence-900 mb-4">
                Start a conversation with us
              </h2>
              <p className="text-lg text-intelligence-600 max-w-lg mx-auto mb-8 flex-grow">
                Instead of a static form, we practice what we preach. Tell us about your
                challenges and goals through a short PrismScope conversation&mdash;and experience
                the product at the same time.
              </p>
              <div>
                <a
                  href={SURVEY_URL}
                  className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg shadow-md transition-colors"
                >
                  Start the Conversation
                </a>
                <p className="text-sm text-intelligence-500 mt-4">
                  Takes about 3&ndash;5 minutes. No account required.
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-xl border border-intelligence-200 p-8 md:p-12">
              <div className="w-16 h-16 bg-accent-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Send className="w-8 h-8 text-accent-600" />
              </div>
              <h2 className="text-2xl font-bold text-intelligence-900 mb-4 text-center">
                Send us a message
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-intelligence-700 mb-1">Name *</label>
                  <input
                    id="contact-name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 border border-intelligence-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-intelligence-700 mb-1">Email *</label>
                  <input
                    id="contact-email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 border border-intelligence-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label htmlFor="contact-company" className="block text-sm font-medium text-intelligence-700 mb-1">Company</label>
                  <input
                    id="contact-company"
                    type="text"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="w-full px-4 py-3 border border-intelligence-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-intelligence-700 mb-1">Message *</label>
                  <textarea
                    id="contact-message"
                    required
                    rows={4}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full px-4 py-3 border border-intelligence-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                {status.message && (
                  <p className={`text-sm ${status.type === 'success' ? 'text-prism-emerald' : 'text-prism-red'}`}>
                    {status.message}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium transition-colors disabled:opacity-50"
                >
                  {submitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>
          </div>

          {/* Location */}
          <div className="mt-12 text-center">
            <div className="inline-flex items-center text-intelligence-500">
              <MapPin className="w-5 h-5 text-primary-600 mr-2" />
              <span className="text-intelligence-900 font-medium">PrismScope</span>
              <span className="mx-2">&middot;</span>
              <span>Madison, Wisconsin</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
